// Constantes de documentos (ClientDocument). Espejan el modelo del backend.

export const DOC_STATUS = {
    PENDING: 'PENDING',
    EXTRACTING: 'EXTRACTING',
    READY: 'READY',
    FAILED: 'FAILED',
};

export const DOC_SOURCE = {
    MEETING: 'MEETING',
    EMAIL: 'EMAIL',
    DRIVE: 'DRIVE',
    NOTION: 'NOTION',
    MANUAL: 'MANUAL',
    SESSION: 'SESSION',
    // Lo estampa el BACKEND cuando el material resulta ser un enlace: el FE nunca manda URL al
    // crear (manda MANUAL), pero sí lo lee para ofrecer "Actualizar desde la URL".
    URL: 'URL',
    // Lo estampa el BACKEND cuando el material entró como archivo binario por el endpoint
    // multipart (`client-document/upload/`). El FE nunca lo manda: el JSON de siempre lo rechaza.
    UPLOAD: 'UPLOAD',
};

// Rechazos tipados de la subida de archivos (backend `upload_reasons.py`). Mismo reparto que
// `URL_ERROR_KEYS`: el backend manda `reason_code`, acá se mapea a i18n; un code desconocido cae al
// genérico. Los textos interpolan `{{name}}` (el archivo) y, el genérico, `{{formats}}`.
export const UPLOAD_ERROR_KEYS = {
    MISSING_FILE: 'client_memory.upload_error.unsupported_type',
    TOO_MANY_FILES: 'client_memory.upload_error.unsupported_type',
    EMPTY_FILE: 'client_memory.upload_error.empty_file',
    TOO_LARGE: 'client_memory.upload_error.too_large',
    UNSUPPORTED_TYPE: 'client_memory.upload_error.unsupported_type',
    ENCRYPTED_PDF: 'client_memory.upload_error.encrypted_pdf',
    TOO_MANY_PAGES: 'client_memory.upload_error.too_many_pages',
    INVALID_FILE: 'client_memory.upload_error.invalid_file',
    // Un `.txt` gigante: el mismo texto pegado se rechaza en el POST, así que el archivo también.
    TEXT_TOO_LONG: 'client_memory.upload_error.text_too_long',
    PER_CLIENT_LIMIT: 'client_memory.upload_error.per_client_limit',
    DUPLICATE: 'client_memory.upload_error.duplicate',
    UPLOAD_DISABLED: 'client_memory.upload_error.upload_disabled',
    STORAGE_ERROR: 'client_memory.upload_error.storage_error',
    AUDIO_DISABLED: 'client_memory.upload_error.audio_disabled',
    AUDIO_TOO_LONG: 'client_memory.upload_error.audio_too_long',
    BLOB_MISSING: 'client_memory.upload_error.blob_missing',
};

// `UNSUPPORTED_TYPE` puede venir con `detected` (doc / xlsx / pptx / gif): ahí el backend sabe qué
// es y el texto puede dar la pista. Sin `detected`, el genérico.
const DETECTED_KEYS = {
    doc: 'client_memory.upload_error.export_to_pdf',
    xlsx: 'client_memory.upload_error.spreadsheet',
    pptx: 'client_memory.upload_error.export_to_pdf',
    gif: 'client_memory.upload_error.gif',
};

// Errores de TRANSPORTE que no traen `reason_code` del backend: los arma `utils/multipart.js`
// leyendo el status HTTP. Sin este mapeo caían los dos en el toast genérico, que además ofrece
// reintentar — para un 401 eso no puede funcionar nunca y para un 429 empeora el throttle.
const TRANSPORT_ERROR_KEYS = {
    UNAUTHORIZED: 'client_memory.upload_error.unauthorized',
    RATE_LIMITED: 'client_memory.upload_error.rate_limited',
};

/** Key i18n para una respuesta de error del endpoint de subida; `null` si no es un code conocido. */
export const uploadErrorKeyFor = (response) => {
    if (!response) return null;
    const code = response.reason_code;
    if (code === 'UNSUPPORTED_TYPE') {
        const detected = response.raw_body && response.raw_body.detected;
        return DETECTED_KEYS[detected] || UPLOAD_ERROR_KEYS.UNSUPPORTED_TYPE;
    }
    // El duplicado nombra al documento que ya está cargado cuando el backend lo manda: «ya está
    // como "Brief de octubre"» le dice al usuario dónde buscarlo, «ya está» lo deja adivinando.
    if (code === 'DUPLICATE') {
        const existing = response.raw_body && response.raw_body.existing_document;
        return existing && existing.title
            ? 'client_memory.upload_error.duplicate_named'
            : UPLOAD_ERROR_KEYS.DUPLICATE;
    }
    if (UPLOAD_ERROR_KEYS[code]) return UPLOAD_ERROR_KEYS[code];
    return TRANSPORT_ERROR_KEYS[response.error_code] || null;
};

/**
 * Los valores que interpolan los textos de rechazo, sacados del body del backend.
 *
 * ÚNICO lugar donde se traducen los `extra` del backend a las variables de i18n, porque las dos
 * superficies que muestran estos errores (la tira de la Barra Total y la pantalla «Cargar»)
 * los armaban por su cuenta y divergieron: una mapeaba sólo `max_bytes` y la otra ninguno, así que
 * en pantalla salían «el máximo es .» y «llegó al máximo de  archivos». i18next no avisa de una
 * interpolación faltante (no hay `missingInterpolationHandler`): deja el `{{…}}` crudo.
 *
 * El backend mete los `extra` como claves de PRIMER NIVEL del body (`_bad_request` hace
 * `payload.update(extra)`): `max_bytes` en TOO_LARGE, `max_pages` en TOO_MANY_PAGES, `max` en
 * PER_CLIENT_LIMIT, y `existing_document` en DUPLICATE.
 */
export const uploadErrorParamsFor = (response, {name = '', formats = ''} = {}) => {
    const body = (response && response.raw_body) || {};
    const params = {name, formats};
    // La conversión a MB vive acá y no en el copy: el backend habla en bytes y el texto en MB.
    if (body.max_bytes) params.max = Math.round(body.max_bytes / (1024 * 1024));
    if (body.max != null) params.max = body.max;
    if (body.max_pages != null) params.max_pages = body.max_pages;
    if (body.max_seconds != null) params.max_minutes = Math.round(body.max_seconds / 60);
    const existing = body.existing_document;
    if (existing && existing.title) params.title = existing.title;
    return params;
};

// Rechazos que NO cambian por reintentar el MISMO archivo: los del archivo (formato, tamaño,
// páginas, duplicado…) y los dos kill-switches, que son permanentes aunque viajen como 503. Ofrecer
// «Reintentar» ahí es prometer algo que no puede funcionar; el camino real es otro archivo, o
// esperar a que la feature se prenda. Lo transitorio (el bucket caído, la red) sí se reintenta.
const NON_RETRYABLE_UPLOAD_ERRORS = new Set([
    'MISSING_FILE', 'TOO_MANY_FILES', 'EMPTY_FILE', 'TOO_LARGE', 'UNSUPPORTED_TYPE',
    'ENCRYPTED_PDF', 'TOO_MANY_PAGES', 'INVALID_FILE', 'TEXT_TOO_LONG', 'PER_CLIENT_LIMIT',
    'DUPLICATE', 'UPLOAD_DISABLED', 'AUDIO_DISABLED', 'AUDIO_TOO_LONG', 'BLOB_MISSING',
]);

/** ¿Tiene sentido ofrecer «Reintentar» para este rechazo? */
export const isRetryableUploadError = ({reasonCode, errorCode} = {}) => {
    if (errorCode === 'UNAUTHORIZED') return false;   // la sesión venció: reintentar da lo mismo
    return !NON_RETRYABLE_UPLOAD_ERRORS.has(reasonCode);
};

// Rechazos tipados de la lectura de enlaces (backend `url_reasons.py`): al cargar un enlace y al
// actualizar un documento desde su página, el backend responde 400 con `reason_code`. Sin este
// mapeo el motivo muere en el toast genérico. Un code desconocido (skew de deploy, código nuevo)
// cae al genérico solo.
export const URL_ERROR_KEYS = {
    INVALID_URL: 'client_memory.setup.url_error.invalid_url',
    PRIVATE_ADDRESS: 'client_memory.setup.url_error.private_address',
    UNREACHABLE: 'client_memory.setup.url_error.unreachable',
    HTTP_ERROR: 'client_memory.setup.url_error.http_error',
    UNSUPPORTED_CONTENT: 'client_memory.setup.url_error.unsupported_content',
    TEXT_TOO_LONG: 'client_memory.setup.url_error.text_too_long',
    TOO_MANY_REDIRECTS: 'client_memory.setup.url_error.unreachable',
    EMPTY_CONTENT: 'client_memory.setup.url_error.empty_content',
    MULTIPLE_URLS: 'client_memory.setup.url_error.multiple_urls',
    FETCH_DISABLED: 'client_memory.setup.url_error.fetch_disabled',
    // Los dos últimos sólo los devuelve el refresh, y NO van a un toast: la vista de documentos
    // los convierte en una confirmación con el dato del rechazo (ver `handleRefreshUrl`). Quedan
    // mapeados igual para que, si algún día los recibe otra pantalla, digan algo en vez de caer
    // al genérico.
    CONTENT_SHRUNK: 'client_memory.setup.url_error.content_shrunk',
    URL_REDIRECTED: 'client_memory.setup.url_error.url_redirected',
};

// Lectura parcial: qué NO quedó en el texto del documento (backend `content_gaps.py`). Cada gap
// viene como `{reason_code, count, detail}`. Mismo reparto que `URL_ERROR_KEYS`: el backend manda el
// code, acá se mapea a i18n. Un code desconocido (skew de deploy, código nuevo) cae al genérico.
//
// ⚠️ El backend manda además `content_gaps_notice`, una frase ya armada — **no se muestra**: es voz
// server-side pensada para el agente del MCP. La pantalla arma su propio texto desde los codes.
export const CONTENT_GAP_KEYS = {
    EMBEDDED_IMAGE: 'client_memory.content_gap.embedded_image',
    NO_TEXT_LAYER: 'client_memory.content_gap.no_text_layer',
    EXTRA_SHEET_TABS: 'client_memory.content_gap.extra_sheet_tabs',
    TRUNCATED_SOURCE: 'client_memory.content_gap.truncated_source',
    FACTS_CAPPED: 'client_memory.content_gap.facts_capped',
    // Un audio subido sin habla inteligible: el backend lo marca FAILED con este descarte.
    INAUDIBLE_AUDIO: 'client_memory.content_gap.inaudible_audio',
    OTHER: 'client_memory.content_gap.other',
};

export const contentGapLabelKey = (code) => CONTENT_GAP_KEYS[code] || CONTENT_GAP_KEYS.OTHER;
// El "por qué" de cada motivo — es lo que convierte el aviso en algo accionable en vez de un cartel.
export const contentGapWhyKey = (code) => `${contentGapLabelKey(code)}_why`;

// `content_gaps` ausente ⇒ el documento no dice nada (no "0 problemas"): el backend puede no estar
// deployado todavía. Mismo criterio que `facts_extracted_count` en la lista de ingesta.
export const documentGaps = (doc) => (
    doc && Array.isArray(doc.content_gaps) ? doc.content_gaps : []);

// Modificadores CSS por estado de extracción (color del badge). Color + texto siempre.
export const DOC_STATUS_MODIFIER = {
    PENDING: '-pending',
    EXTRACTING: '-extracting',
    READY: '-ready',
    FAILED: '-failed',
};

// Límite duro del crudo (espeja MAX_TRANSCRIPT_CHARS del backend).
export const MAX_TRANSCRIPT_CHARS = 200000;

export const docStatusLabelKey = (s) => `client_memory.doc_status.${(s || 'PENDING').toLowerCase()}`;
export const docSourceLabelKey = (s) => `client_memory.doc_source.${(s || 'MEETING').toLowerCase()}`;

/**
 * Marcadores de que un `summary_error` es un error TÉCNICO y no un motivo para una persona.
 *
 * El backend guarda en `_summary_error` lo que haya reventado, y cuando lo que revienta es la base
 * de datos ahí queda el mensaje del motor entero. En pantalla eso son nueve líneas rojas que
 * desbordan la celda, nombran tablas y columnas internas y —lo peor— **incluyen la fila que falló**,
 * o sea el texto de un hecho del cliente (QA 2026-09-07, QA-VIS-cola-06).
 *
 * Se resuelve en el FRONTEND y no sólo en el backend porque el campo está PERSISTIDO: arreglar quién
 * lo escribe no toca las filas que ya lo tienen guardado, y son justamente las que se están viendo.
 */
const TECHNICAL_ERROR_MARKERS = [
    'violates', 'null value in column', 'relation "', 'DETAIL:', 'Traceback',
    'psycopg', 'django.db', 'IntegrityError', 'ProgrammingError', 'OperationalError',
    'DataError', 'constraint', 'Failing row contains', 'File "', 'Exception',
];

/** ¿Este `summary_error` es un volcado técnico que no se le puede mostrar a nadie? */
export const isTechnicalDocError = (summaryError) => {
    const text = String(summaryError || '');
    if (!text) return false;
    const lower = text.toLowerCase();
    return TECHNICAL_ERROR_MARKERS.some(m => lower.includes(m.toLowerCase()));
};

/**
 * Qué se muestra de un documento fallado: `{message, technical}`.
 *
 * `message` es siempre legible. `technical` viene con el volcado sólo cuando hubo que esconderlo,
 * para que la pantalla pueda ofrecerlo detrás de un gesto — esconderlo del todo dejaría a soporte
 * sin nada, y mostrarlo de entrada es el bug.
 */
export const docErrorParts = (doc, t) => {
    const raw = (doc && doc.summary_error) || '';
    if (!raw) return {message: '', technical: null};
    if (!isTechnicalDocError(raw)) return {message: raw, technical: null};
    return {message: t('client_memory.doc_error_technical'), technical: raw};
};

// Rechazos del refresh que NO son un error: el backend frenó porque la página que contestó no
// parece la que se venía leyendo. Se preguntan (con el dato del rechazo) en vez de avisarse, porque
// sólo el usuario sabe si su página se acortó / se mudó de verdad.
//
// Vive acá y no en la vista porque son DOS las superficies que actualizan un documento desde su
// página: la tabla de la Memoria clásica y la biblioteca del Recorrido. Con una copia en cada una,
// un `reason_code` nuevo del backend se agrega en la que se está tocando y la otra lo manda al
// toast genérico — que es justo lo que este mapa vino a evitar.
export const REFRESH_CONFIRM_REASONS = {
    CONTENT_SHRUNK: {
        title: 'client_memory.documents.refresh_url_shrunk_title',
        question: 'client_memory.documents.refresh_url_shrunk_question',
    },
    URL_REDIRECTED: {
        title: 'client_memory.documents.refresh_url_redirected_title',
        question: 'client_memory.documents.refresh_url_redirected_question',
    },
};
